import { useTranslation } from 'react-i18next'
import { Icon } from '@mysaiva/core'
import type { IconName } from '@mysaiva/core/icons'
import { useOrg } from '@mysaiva/core/organizations'
import ArrowDotted from '../../assets/arrow-dotted.svg?react'
import DashMark from '../../assets/governance-dash.svg?react'
import styles from './Dashboard.module.scss'

type Level = { id: 'organization' | 'facility' | 'patient'; icon: IconName }

const LEVELS: Level[] = [
  { id: 'organization', icon: 'Suitcase' },
  { id: 'facility', icon: 'Building' },
  { id: 'patient', icon: 'Key' }
]

const GUARANTEES = ['scopes', 'audit', 'readOnly', 'rotate'] as const

/**
 * "Access & governance, built in": the three-level access model
 * (Organization → Facility → Patient) next to the four governance
 * guarantees. The organisation tile shows the selected organisation; the
 * facility and patient tiles describe how keys are scoped, as the backend
 * exposes no per-org facility approval count yet. Markers follow the design
 * review (SAIV-11493): dashed blue in light mode, green check in dark mode.
 */
export function GovernanceSection() {
  const { t } = useTranslation()
  const { org } = useOrg()

  return (
    <section
      className={`${styles.panel} ${styles.governance}`}
      aria-labelledby="dp-governance-title"
      data-testid="dp-governance"
    >
      <div className={styles.governanceModel}>
        <h2 id="dp-governance-title" className={styles.panelTitle}>
          {t('developerPlatform.dashboard.governance.title')}
        </h2>
        <p className={styles.governanceLead}>{t('developerPlatform.dashboard.governance.lead')}</p>
        <ol
          className={styles.levels}
          aria-label={t('developerPlatform.dashboard.governance.modelLabel')}
        >
          {LEVELS.map((level, index) => (
            <li key={level.id} style={{ display: 'contents' }}>
              {index > 0 && (
                <span className={styles.levelArrow} aria-hidden="true">
                  <ArrowDotted />
                </span>
              )}
              <div className={styles.level} data-testid={`dp-governance-level-${level.id}`}>
                <Icon icon={level.icon} size={34} className={styles.levelIcon} />
                <div className={styles.levelText}>
                  <p className={styles.levelName}>
                    {t(`developerPlatform.dashboard.governance.levels.${level.id}.name`)}
                  </p>
                  <p className={styles.levelDetail}>
                    {level.id === 'organization'
                      ? org?.name
                      : t(`developerPlatform.dashboard.governance.levels.${level.id}.detail`)}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <ul className={styles.guarantees}>
        {GUARANTEES.map(key => (
          <li key={key} className={styles.guarantee}>
            {/* Light mode: the design's dashed marker; dark mode: green check in a well. */}
            <span className={styles.check} aria-hidden="true">
              <Icon icon="CheckCircle" size={14} />
            </span>
            <span className={styles.dash} aria-hidden="true">
              <DashMark />
            </span>
            <span>{t(`developerPlatform.dashboard.governance.guarantees.${key}`)}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}
